import { ReactNode } from 'react';
import { useOktaAuth } from '@okta/okta-react';
import type { User } from '@/common-types';
import * as userService from '@/services/api/user-service';
import { useState, useEffect, useCallback, useRef } from 'react';
import { AuthContext } from './auth-context';
import { PERMISSIONS } from '@/common-constants/permissions';

/** Normalize permissions to string[] whether API returns strings or objects with id (e.g. { id: "read", description: "..." }). */
function normalizePermissions(permissions: unknown): string[] {
  if (!Array.isArray(permissions)) return [];
  return permissions.map((p) => (typeof p === 'string' ? p : (p as { id?: string })?.id)).filter(Boolean) as string[];
}

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const { oktaAuth, authState } = useOktaAuth();
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const isAuthenticated = authState?.isAuthenticated ?? false;

  const refreshUser = useCallback(async () => {
    if (!isAuthenticated) {
      setUser(null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const userData = await userService.getCurrentUser();
      const normalized: User = {
        ...userData,
        permissions: normalizePermissions(userData.permissions ?? []),
      };
      setUser(normalized);
    } catch (err) {
      console.error('Failed to refresh user:', err);
      setError(err instanceof Error ? err.message : 'Failed to refresh user');
      setUser(null);
      if (err instanceof Error && (err.message.includes('401') || err.message.includes('403'))) {
        oktaAuth.signOut();
      }
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, oktaAuth]);

  const refreshUserRef = useRef(refreshUser);
  useEffect(() => {
    refreshUserRef.current = refreshUser;
  }, [refreshUser]);

  const userFetchingRef = useRef(false);

  useEffect(() => {
    // authState is null while Okta is still resolving (e.g. on a hard reload).
    // We must not set loading=false or clear user until Okta has confirmed the
    // session, otherwise AppRoutes briefly sees isAuthenticated=false and
    // redirects to /login before the token check completes.
    if (authState === null) return;

    if (isAuthenticated && !user) {
      if (userFetchingRef.current) return;
      userFetchingRef.current = true;
      refreshUserRef.current().finally(() => {
        userFetchingRef.current = false;
      });
    } else if (!isAuthenticated) {
      setUser(null);
      setLoading(false);
    }
  }, [authState, isAuthenticated, user]);

  const login = useCallback(async (originalUri?: string) => {
    try {
      setError(null);
      if (originalUri) {
        await oktaAuth.signInWithRedirect({ originalUri });
        return;
      }
      await oktaAuth.signInWithRedirect();
    } catch (err) {
      console.error('Login error:', err);
      setError(err instanceof Error ? err.message : 'Login failed');
    }
  }, [oktaAuth]);

  const logout = useCallback(async () => {
    setUser(null);
    await oktaAuth.signOut();
  }, [oktaAuth]);

  const isAdmin = Boolean(user?.permissions?.includes(PERMISSIONS.ADMIN));

  const hasPermission = useCallback((permissionId: string): boolean => {
    if (!user?.permissions) return false;
    if (user.permissions.includes(PERMISSIONS.ADMIN)) return true;
    return user.permissions.includes(permissionId);
  }, [user]);

  return (
    <AuthContext.Provider value={{
      user,
      loading,
      isAdmin,
      isAuthenticated,
      error,
      hasPermission,
      login,
      logout,
      refreshUser
    }}>
      {children}
    </AuthContext.Provider>
  );
};
